import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enAuth from './en/auth.json';
import esAuth from './es/auth.json';
import enCommon from './en/common.json';
import esCommon from './es/common.json';
import enCanvas from './en/canvas.json';
import esCanvas from './es/canvas.json';
import enBilling from './en/billing.json';
import esBilling from './es/billing.json';
import enSettings from './en/settings.json';
import esSettings from './es/settings.json';
import enEmailGen from './en/emailGen.json';
import esEmailGen from './es/emailGen.json';
import enDashboard from './en/dashboard.json';
import esDashboard from './es/dashboard.json';
import enTemplates from './en/templates.json';
import esTemplates from './es/templates.json';
import enAnalytics from './en/analytics.json';
import esAnalytics from './es/analytics.json';
import enOnboarding from './en/onboarding.json';
import esOnboarding from './es/onboarding.json';
import enMediaLibrary from './en/mediaLibrary.json';
import esMediaLibrary from './es/mediaLibrary.json';
import enEmailBuilder from './en/emailBuilder.json';
import esEmailBuilder from './es/emailBuilder.json';
import enFlows from './en/flows.json';
import enInspo from './en/inspo.json';
import esFlows from './es/flows.json';
import esInspo from './es/inspo.json';
import enWhiteGlove from './en/whiteGlove.json';
import esWhiteGlove from './es/whiteGlove.json';
import enNotifications from './en/notifications.json';
import esNotifications from './es/notifications.json';

// ----------------------------------------------------------------------

export const SUPPORTED_LANGUAGES = ['en', 'es'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const FALLBACK_LANGUAGE: SupportedLanguage = 'en';

export const LANGUAGE_STORAGE_KEY = 'nexmail_language';

/**
 * Domain namespaces. Only namespaces with loaded resources below render
 * translations today; the rest fall back to `en` (or the inline default
 * passed to `t()`) until their catalogs land in later phases. The admin
 * tree is intentionally excluded — it stays English.
 */
export const NAMESPACES = [
  'common',
  'auth',
  'dashboard',
  'settings',
  'campaigns',
  'templates',
  'analytics',
  'billing',
  'onboarding',
  'mediaLibrary',
  'canvas',
  'emailBuilder',
  'emailGen',
  'flows',
  'whiteGlove',
  'notifications',
  'inspo',
  'validation',
] as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        settings: enSettings,
        auth: enAuth,
        dashboard: enDashboard,
        templates: enTemplates,
        analytics: enAnalytics,
        billing: enBilling,
        onboarding: enOnboarding,
        mediaLibrary: enMediaLibrary,
        canvas: enCanvas,
        emailBuilder: enEmailBuilder,
        emailGen: enEmailGen,
        flows: enFlows,
        notifications: enNotifications,
        whiteGlove: enWhiteGlove,
        inspo: enInspo,
      },
      es: {
        common: esCommon,
        settings: esSettings,
        auth: esAuth,
        dashboard: esDashboard,
        templates: esTemplates,
        analytics: esAnalytics,
        billing: esBilling,
        onboarding: esOnboarding,
        mediaLibrary: esMediaLibrary,
        canvas: esCanvas,
        emailBuilder: esEmailBuilder,
        emailGen: esEmailGen,
        flows: esFlows,
        notifications: esNotifications,
        whiteGlove: esWhiteGlove,
        inspo: esInspo,
      },
    },
    fallbackLng: FALLBACK_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    ns: [...NAMESPACES],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    returnNull: false,
    detection: {
      // Pre-auth boot: trust the persisted choice, then the browser.
      // The authed `users.language` is applied by LocaleProvider once known.
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
  });

export default i18n;
